import { Button, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import React from 'react';
import { DefaultExtensionType, defaultStyles, FileIcon } from 'react-file-icon';
import { minifyBytes } from '../../utils/conversion';
import { Close, Pause, PlayArrow } from '@mui/icons-material';
import { convertFileFormatToText } from '../../utils/string';

interface UploadStatusProps {
	chosenFile: File | null;
	uploadProgressPercent: number;
	uploadStarted: boolean;
	onUploadStart: () => void;
	submitDisabled?: boolean;
	isPaused: boolean;
	onPauseToggle: (isPaused: boolean) => void;
	onCancel: () => void;
}

const UploadStatusDisplay: React.FC<UploadStatusProps> = ({
	chosenFile,
	uploadProgressPercent,
	uploadStarted,
	onUploadStart,
	submitDisabled,
	isPaused,
	onPauseToggle,
	onCancel,
}) => {
	const getFileIcon = () => {
		const ext = convertFileFormatToText(chosenFile?.type || 'txt');
		if (Object.keys(defaultStyles).includes(ext)) {
			return <FileIcon extension={ext.toLocaleUpperCase()} {...defaultStyles[ext as DefaultExtensionType]} />;
		} else {
			return <FileIcon extension={ext.toLocaleUpperCase()} {...defaultStyles['csv']} />;
		}
	};

	const fileName = chosenFile && chosenFile.name.length > 80 ? `${chosenFile.name.substring(0, 100)}...` : chosenFile?.name;

	return (
		chosenFile && (
			<Stack width="100%" sx={{ borderStyle: 'solid', borderWidth: 2, borderColor: 'action.disabled', borderRadius: 1, padding: 1 }}>
				<Stack direction={'row'} justifyContent={'space-between'}>
					<Stack direction={'row'} alignContent={'center'} spacing={2}>
						<Box width="40px" padding={0.5} height={'48px'}>
							{getFileIcon()}
						</Box>
						<Stack maxWidth={'100%'}>
							<Typography variant="body1" fontWeight={'bold'} data-testid="file-upload-filename">
								{fileName}
							</Typography>
							<Typography variant="body2">{minifyBytes(chosenFile?.size || 0)}</Typography>
						</Stack>
					</Stack>

					<Stack direction="row" alignContent={'center'} spacing={2}>
						{uploadStarted && uploadProgressPercent < 100 && (
							<Tooltip title={isPaused ? 'Resume Upload' : 'Pause Upload'}>
								{isPaused ? (
									<IconButton onClick={() => onPauseToggle(false)} data-testid="upload-resume-button">
										<PlayArrow />
									</IconButton>
								) : (
									<IconButton onClick={() => onPauseToggle(true)} data-testid="upload-pause-button">
										<Pause />
									</IconButton>
								)}
							</Tooltip>
						)}

						<Tooltip title="Cancel Upload">
							<IconButton onClick={onCancel} data-testid="upload-cancel-button">
								<Close />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>

				{uploadStarted ? (
					<Box sx={{ display: 'flex', alignItems: 'center' }} paddingX={1}>
						<Box sx={{ width: '100%', marginRight: 2 }}>
							<LinearProgress
								variant="determinate"
								value={uploadProgressPercent}
								data-testid="upload-progress-bar"
								aria-label={`Upload Progress Bar (${uploadProgressPercent}%) `}
							/>
						</Box>
						<Box sx={{ minWidth: '40px' }}>
							<Typography>{uploadProgressPercent.toFixed(2)}%</Typography>
						</Box>
					</Box>
				) : (
					<Box paddingY={1}>
						<Button onClick={onUploadStart} fullWidth variant="contained" data-testid="upload-submit-button" disabled={submitDisabled}>
							Submit
						</Button>
					</Box>
				)}
			</Stack>
		)
	);
};

export default UploadStatusDisplay;
